import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Sparkles, Loader2 } from 'lucide-react';
import api from '@/api';

const STEPS = [
  { key: 'welcome', title: 'Welcome' },
  { key: 'purposes', title: 'Purpose' },
  { key: 'tracked', title: 'Track' },
  { key: 'alerts', title: 'Alerts' },
  { key: 'persona', title: 'You' },
];

// Screen 1 — What are you here for? (May 2026 redesign)
const INTERESTS = [
  'Reselling', 'Investing', 'Trading',
  'Side Hustles', 'Trend Spotting', 'Just Exploring',
];

// Screen 2 — What do you track?
const CURIOSITY_TYPES = [
  'eBay', 'TikTok Shop', 'Amazon FBA',
  'Sneakers', 'Stocks', 'Crypto',
];

// Screen 3 — What alerts do you want? (replaces the legacy location step)
const ALERTS = [
  'Viral Products', 'Undervalued Flips', 'Fast-Rising Demand',
  'Breakout Stocks', 'Crypto Moves', 'Trend Forecasts',
];

// Screen 4 — Which best describes you?
const PROFESSIONAL_OPTIONS = [
  'Student', 'Professional', 'Entrepreneur',
  'Buyer & Seller', 'Investor / Trader', 'Content Creator',
];

const TOPIC_GROUPS = [];
const ALL_SUGGESTED = [];

const COUNTRIES = [
  'United States', 'United Kingdom', 'India',
];

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_web-pulse-4/artifacts/i5z1wfjq_WTFH%20logo.png';

export default function OnboardingFlow({ user, onComplete, onLogout, editMode = false }) {
  const prefs = user?.preferences || {};
  // ISO/short-code → canonical country name mapping. DB historically stores
  // 'UK' / 'US' / 'IN' but the dropdown options are full names. Normalise on
  // read so the country dropdown reflects the saved preference (May 2026 fix).
  const _normaliseCountry = (val) => {
    if (!val || typeof val !== 'string') return '';
    const lc = val.toLowerCase().trim();
    const codeMap = {
      uk: 'United Kingdom', gb: 'United Kingdom', 'united kingdom': 'United Kingdom',
      us: 'United States', usa: 'United States', 'united states': 'United States',
      in: 'India', ind: 'India', india: 'India',
    };
    return codeMap[lc] || val;
  };
  // Case-insensitive normalisation for ARRAY-fields (interests, curiosity,
  // professional_context). Historical user docs stored these as lowercase
  // ("technology"), but the chip labels here are TitleCase ("Technology").
  // Without this normalisation, `interests.includes(item)` always returns
  // false in editMode → user thinks their preferences vanished.
  const _matchCanonical = (saved, canonicalList) => {
    if (!Array.isArray(saved)) return [];
    const lcMap = Object.fromEntries(canonicalList.map(c => [c.toLowerCase(), c]));
    return saved
      .map(s => (typeof s === 'string' ? (lcMap[s.toLowerCase()] || s) : s))
      .filter(Boolean);
  };
  const [step, setStep] = useState(editMode ? 1 : 0);
  const [interests, setInterests] = useState(editMode ? _matchCanonical(prefs.interests, INTERESTS) : []);
  const [curiosityTypes, setCuriosityTypes] = useState(editMode ? _matchCanonical(prefs.curiosity_types, CURIOSITY_TYPES) : []);
  const [depth, setDepth] = useState(editMode ? (prefs.explanation_depth || 'simple') : 'simple');
  const [country, setCountry] = useState(editMode ? _normaliseCountry(prefs.country) : '');
  const [region, setRegion] = useState(editMode ? (prefs.region || '') : '');
  const [professional, setProfessional] = useState(() => {
    if (!editMode) return [];
    const ctx = prefs.professional_context;
    if (Array.isArray(ctx)) return _matchCanonical(ctx, PROFESSIONAL_OPTIONS);
    if (typeof ctx === 'string') return _matchCanonical(ctx.split(', ').filter(Boolean), PROFESSIONAL_OPTIONS);
    return [];
  });
  const [followedTopics, setFollowedTopics] = useState(editMode ? _matchCanonical(prefs.followed_topics, ALERTS) : []);
  const [saving, setSaving] = useState(false);

  const toggleItem = (list, setList, item) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const canProceed = () => {
    if (step === 0) return true; // welcome
    if (step === 1) return interests.length > 0;        // purposes
    if (step === 2) return curiosityTypes.length > 0;   // tracked
    if (step === 3) return followedTopics.length > 0;   // alerts
    if (step === 4) return true;                        // persona (optional)
    return true;
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await api.saveOnboarding({
        interests,
        curiosity_types: curiosityTypes,
        explanation_depth: depth,
        country,
        region,
        professional_context: professional.join(', '),
        followed_topics: followedTopics,
      });
      const updatedUser = { ...user, onboarding_complete: true, preferences: {
        interests, curiosity_types: curiosityTypes, explanation_depth: depth,
        country, region, professional_context: professional, followed_topics: followedTopics,
      }};
      localStorage.setItem('wtf_user', JSON.stringify(updatedUser));
      onComplete(updatedUser);
    } catch (e) {
      console.error('Onboarding save failed:', e);
      // Still proceed
      onComplete({ ...user, onboarding_complete: true });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (step === STEPS.length - 1) {
      handleFinish();
    } else {
      setStep(s => s + 1);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div data-testid="onboarding-flow" className="min-h-screen flex flex-col px-5 py-6">
      {/* Progress bar */}
      {step > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <button
              data-testid="onboarding-back"
              onClick={() => {
                if (editMode && step === 1) { onLogout(); return; }
                setStep(s => s - 1);
              }}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center
                hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={16} className="text-gray-600" />
            </button>
            <span className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">
              {step} / {STEPS.length - 1}
            </span>
          </div>
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gray-900 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      )}

      {/* Welcome step: back/logout button */}
      {step === 0 && (
        <div className="flex justify-end">
          <button
            data-testid="onboarding-logout"
            onClick={onLogout}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Sign Out
          </button>
        </div>
      )}

      {/* Step content */}
      <div className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
          >
            {/* Step 0: Welcome */}
            {step === 0 && (
              <div className="flex flex-col items-center justify-center min-h-[70vh] text-center">
                <motion.img
                  src={LOGO_URL}
                  alt="WTFHappened"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                  className="h-32 sm:h-36 mx-auto mb-8 object-contain"
                />
                <h1 className="text-4xl sm:text-5xl font-black text-white leading-[1.1] mb-5">
                  Understand what's<br />happening in the world<br />in 30 seconds
                </h1>
                <p className="text-lg text-white/90 max-w-[340px] mx-auto mb-3 leading-relaxed font-semibold">
                  We explain trending topics with 3 simple cards.
                  No noise, just clarity.
                </p>
                <p className="text-lg text-gray-600">
                  Let's personalize your feed.
                </p>

                {/* Visual feature pills */}
                <div className="grid grid-cols-3 gap-4 mt-10 w-full max-w-lg">
                  {[
                    { emoji: '\u26A1', text: '3-card explainers' },
                    { emoji: '\uD83D\uDD25', text: 'Trending topics' },
                    { emoji: '\uD83E\uDD16', text: 'AI-powered' },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + i * 0.15 }}
                      className="flex flex-col items-center gap-3 px-4 py-6 rounded-2xl bg-white border border-gray-100 shadow-sm"
                    >
                      <span className="text-4xl">{item.emoji}</span>
                      <span className="text-sm font-bold text-gray-700 text-center">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Screen 1 — What are you here for? (multi-select chips, May 2026) */}
            {step === 1 && (
              <div className="flex flex-col min-h-[70vh] justify-center">
                <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">What are you here for?</h2>
                <p className="text-base text-white/80 font-semibold mb-8">Pick all that apply.</p>
                <div className="flex flex-wrap gap-3">
                  {INTERESTS.map(item => (
                    <button
                      key={item}
                      data-testid={`interest-${item.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => toggleItem(interests, setInterests, item)}
                      className={`px-6 py-3.5 rounded-2xl text-base font-black transition-all duration-150
                        ${interests.includes(item)
                          ? 'bg-white text-black shadow-sm'
                          : 'bg-[#1A1A1A] border-2 text-white hover:brightness-125'}`}
                      style={interests.includes(item) ? {} : { borderColor: 'rgba(255,255,255,0.22)', fontFamily: 'Manrope, sans-serif' }}
                    >
                      {interests.includes(item) && <Check size={16} className="inline mr-1.5" />}
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Screen 2 — What do you track? (multi-select chips) */}
            {step === 2 && (
              <div className="flex flex-col min-h-[70vh] justify-center">
                <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">What do you track?</h2>
                <p className="text-base text-white/80 font-semibold mb-8">Pick all that apply.</p>
                <div className="flex flex-wrap gap-3">
                  {CURIOSITY_TYPES.map(item => (
                    <button
                      key={item}
                      data-testid={`curiosity-${item.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                      onClick={() => toggleItem(curiosityTypes, setCuriosityTypes, item)}
                      className={`px-6 py-3.5 rounded-2xl text-base font-black transition-all duration-150
                        ${curiosityTypes.includes(item)
                          ? 'bg-white text-black shadow-sm'
                          : 'bg-[#1A1A1A] border-2 text-white hover:brightness-125'}`}
                      style={curiosityTypes.includes(item) ? {} : { borderColor: 'rgba(255,255,255,0.22)', fontFamily: 'Manrope, sans-serif' }}
                    >
                      {curiosityTypes.includes(item) && <Check size={16} className="inline mr-1.5" />}
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Screen 3 — What alerts do you want? (multi-select chips, replaces legacy
                location step; the field maps to followed_topics in the prefs schema). */}
            {step === 3 && (
              <div className="flex flex-col min-h-[70vh] justify-center">
                <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">What alerts do you want?</h2>
                <p className="text-base text-white/80 font-semibold mb-8">Pick all that apply.</p>
                <div className="flex flex-wrap gap-3">
                  {ALERTS.map(item => (
                    <button
                      key={item}
                      data-testid={`alert-${item.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                      onClick={() => toggleItem(followedTopics, setFollowedTopics, item)}
                      className={`px-6 py-3.5 rounded-2xl text-base font-black transition-all duration-150
                        ${followedTopics.includes(item)
                          ? 'bg-white text-black shadow-sm'
                          : 'bg-[#1A1A1A] border-2 text-white hover:brightness-125'}`}
                      style={followedTopics.includes(item) ? {} : { borderColor: 'rgba(255,255,255,0.22)', fontFamily: 'Manrope, sans-serif' }}
                    >
                      {followedTopics.includes(item) && <Check size={16} className="inline mr-1.5" />}
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Screen 4 — Which best describes you? (multi-select chips) */}
            {step === 4 && (
              <div className="flex flex-col min-h-[70vh] justify-center">
                <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">Which best describes you?</h2>
                <p className="text-base text-white/80 font-semibold mb-8">Pick all that apply. Optional.</p>
                <div className="flex flex-wrap gap-3">
                  {PROFESSIONAL_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      data-testid={`professional-${opt.toLowerCase().replace(/[\s\/&]/g, '-').replace(/--+/g, '-')}`}
                      onClick={() => toggleItem(professional, setProfessional, opt)}
                      className={`px-6 py-3.5 rounded-2xl text-base font-black transition-all duration-150
                        ${professional.includes(opt)
                          ? 'bg-white text-black shadow-sm'
                          : 'bg-[#1A1A1A] border-2 text-white hover:brightness-125'}`}
                      style={professional.includes(opt) ? {} : { borderColor: 'rgba(255,255,255,0.22)', fontFamily: 'Manrope, sans-serif' }}
                    >
                      {professional.includes(opt) && <Check size={16} className="inline mr-1.5" />}
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom action */}
      <div className="mt-8 pb-4">
        <button
          data-testid="onboarding-next"
          onClick={handleNext}
          disabled={!canProceed() || saving}
          className={`w-full py-4 rounded-2xl text-base font-bold flex items-center justify-center gap-2
            transition-all duration-200
            ${canProceed() && !saving
              ? 'bg-gray-900 text-white hover:bg-gray-800'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
        >
          {saving ? (
            <Loader2 size={20} className="animate-spin" />
          ) : step === 0 ? (
            <>Get Started <Sparkles size={18} /></>
          ) : step === STEPS.length - 1 ? (
            <>Finish Setup <Check size={18} /></>
          ) : (
            <>Continue <ArrowRight size={18} /></>
          )}
        </button>
        {step > 0 && step < STEPS.length - 1 && step >= 4 && (
          <button
            data-testid="onboarding-skip"
            onClick={handleNext}
            className="w-full py-3 mt-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip this step
          </button>
        )}
      </div>
    </div>
  );
}
